import React, { useState, useEffect } from 'react';
import './Header.css';
import GuideModal from './GuideModal';
import SettingsModal from './SettingsModal';
import AIProcessingStatus from './AIProcessingStatus';
import packageJson from '../../package.json';

const Header = ({ onSelectFiles, onClear, disabled, mode, licenseInfo, onActivateLicense, onDeactivateLicense }) => {
  const [showGuide, setShowGuide] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [hasGuideUpdate, setHasGuideUpdate] = useState(false);

  useEffect(() => {
    // Check for guide updates on component mount
    checkForGuideUpdates();
    
    // Check for updates every 30 minutes
    const interval = setInterval(checkForGuideUpdates, 30 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  const checkForGuideUpdates = async () => {
    try {
      const lastSeenVersion = localStorage.getItem('pixlsizer-guide-version');
      if (!lastSeenVersion) return; // First time user, no need to show update

      const response = await fetch(`https://www.pixlsizer.com/api/guide-content?lastSeenVersion=${lastSeenVersion}`);
      
      // Only process if we got a successful response
      if (response.ok) {
        const data = await response.json();
        if (data.hasUpdate) {
          setHasGuideUpdate(true);
        }
      }
    } catch (error) {
      // Silently fail - don't show errors for update checks
      console.log('Guide update check skipped:', error.message);
    }
  };

  const handleGuideVersionUpdate = (hasUpdate) => {
    setHasGuideUpdate(hasUpdate);
  };

  return (
    <>
    <header className="header glass-card">
      <div className="header-content">
        <div className="logo-section">
          <div className="logo-with-version">
            <img
              src={window.location.protocol === 'file:' ? './sizerlogo.svg' : '/sizerlogo.svg'}
              alt="PixlSizer"
              className="logo-svg"
            />
            <span className="version-badge">v{packageJson.version}</span>
          </div>
          <p className="logo-subtitle">
            Developed by{" "}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                window.electronAPI.openExternal('https://pixlevents.com');
              }}
              style={{
                color: '#777777',
                textDecoration: 'none',
                fontWeight: '500'
              }}
            >
              Pixl Events
            </a>
          </p>
        </div>

        <div className="header-actions-center">
          <button 
            className="glass-button" 
            onClick={onSelectFiles}
            disabled={disabled}
          >
            <svg className="button-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
            </svg>
            Select Images
          </button>
          
          <button 
            className="glass-button glass-button-secondary" 
            onClick={onClear}
            disabled={disabled}
          >
            <svg className="button-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10"></polyline>
              <polyline points="1 20 1 14 7 14"></polyline>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
            </svg>
            Clear and Reset
          </button>
        </div>

        {mode === 'resize' && <AIProcessingStatus />}

        <div className="header-actions-right">
          <button
            className="icon-button"
            onClick={() => setShowSettings(true)}
            disabled={disabled}
            title="Settings"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3"></circle>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
            </svg>
          </button>
          <button
            className={`icon-button icon-button-document ${hasGuideUpdate ? 'has-update' : ''}`}
            onClick={() => setShowGuide(true)}
            disabled={disabled}
            title={hasGuideUpdate ? "View User Guide (New content available!)" : "View User Guide"}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
            {hasGuideUpdate && (
              <span className="update-indicator">!</span>
            )}
          </button>
          {!licenseInfo || licenseInfo.tier === 'free' ? (
            <button 
              className="icon-button icon-button-inactive" 
              onClick={onActivateLicense}
              disabled={disabled}
              title="Click to activate your PixlSizer license"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m21 2-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0 3 3L22 7l-3-3m-3.5 3.5L19 4"></path>
              </svg>
            </button>
          ) : (
            <button 
              className="icon-button icon-button-active" 
              onClick={onDeactivateLicense}
              disabled={disabled}
              title={`Licensed (${licenseInfo.tier === 'partner' ? 'Ambassador' : licenseInfo.tier}). Click to deactivate.`}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                <path d="M7 11V7a5 5 0 0 1 9.9-1"></path>
              </svg>
            </button>
          )}
        </div>
      </div>
    </header>
    <GuideModal
      isOpen={showGuide}
      onClose={() => setShowGuide(false)}
      onVersionUpdate={handleGuideVersionUpdate}
    />
    <SettingsModal
      isOpen={showSettings}
      onClose={() => setShowSettings(false)}
    />
    </>
  );
};

export default Header;